import React, { useEffect, useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import CustomButton from '../../../components/CustomButton';
import { translate } from '../../../services/translate';
import FilterInput from '../../../components/Filter/components/FilterInput';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { DeacParamList } from '../../../typings/Params';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { Pagination } from '../../../components/Pagination';
import MyDataTable from '../../../components/MyDataTable';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import { deacService } from '../../../services/central-api/deac';
import VacancyLevelModal from './VacancyLevelModal';
import { useAuth } from '../../../context/Auth';
import Filter from './Filters';
import DeleteVacancyLevelModal from './DeleteVacancyLevelModal';
import Toast from 'react-native-toast-message';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import { RootNavigation } from '../../../Routes';

export default function VacancyLevels({ navigation }: { navigation: RootNavigation; }) {
    const { user: authenticatedUser } = useAuth();
    const route = useRoute<RouteProp<DeacParamList, 'VacancyLevels'>>();

    const [filters, setFilters] = useFilters<DeacParamList['VacancyLevels']>({
        ...route.params,
        page: Math.max(+route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [selectedVacancyLevel, setSelectedVacancyLevel] = useState<VacancyLevel | undefined>(undefined);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['deacVacancyLevels', filters],
        queryFn: () => deacService.getVacancyLevels(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch, isError, error } = queryResult;

    useEffect(() => {
        if (isError) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }, [error, isError]);

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return (
        <Container>
            <Header
                title={translate('vacancyLevels')}
                actions={<CustomButton
                    text={translate('registerLevel')}
                    onPress={() => {
                        setSelectedVacancyLevel(undefined);
                        setIsModalVisible(true);
                    }}
                />}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredLevels')}
                    zIndex={2}
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                    filter={authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancy_level && <Filter
                        filters={filters}
                        setFilters={setFilters}
                        textFilter={textFilter}
                        setTextFilter={setTextFilter}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('title'),
                                cell: row => row.name,
                                grow: 1,
                            },
                            {
                                name: translate('actingBody'),
                                cell: row => row.actingBody.name,
                                grow: 1,
                            },
                            {
                                name: translate('associatedRole'),
                                selector: row => row.roles?.map(r => r.name).join(', ') ?? '',
                                wrap: true,
                                grow: 4,
                                width: 'auto'
                            },
                            {
                                name: '',
                                cell: row => {
                                    const actions: Action[] = [
                                        {
                                            content: translate('editInformation'),
                                            onPress: () => {
                                                setSelectedVacancyLevel(row);
                                                setIsModalVisible(true);
                                            }
                                        },
                                        {
                                            content: translate('delete'),
                                            variant: 'danger',
                                            onPress: () => {
                                                setSelectedVacancyLevel(row);
                                                setIsDeleteModalVisible(true);
                                            },
                                        }
                                    ];

                                    return (
                                        <DropdownButton actions={actions} />
                                    );
                                },
                                wrap: true,
                                button: true
                            }
                        ]}
                        data={data?.rows || []}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        currentPage={filters.page}
                        totalItems={data?.count || 0}
                        setPage={page => {
                            setFilters(old => ({ ...old, page }));
                            navigation.setParams({ ...route.params, page: page + 1 });
                        }}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                            navigation.setParams({ ...route.params, limit: pageSize, page: 1 });
                        }}
                    />}
                />
            </Card>
            <VacancyLevelModal
                selectedVacancyLevelId={selectedVacancyLevel?.id}
                onConfirm={refetch}
                isModalVisible={isModalVisible}
                setModalVisible={setIsModalVisible}
            />
            {selectedVacancyLevel && <DeleteVacancyLevelModal
                onConfirm={refetch}
                selectedVacancyLevel={selectedVacancyLevel}
                isModalVisible={isDeleteModalVisible}
                setModalVisible={setIsDeleteModalVisible}
            />}
        </Container>
    );
}
